/**
 * Worker (mobile/PWA) shell — sits on /field/*. Different from {@link AppShell}:
 *
 *  - no LeftRail/Breadcrumb (workers don't navigate; at most 2 routes today)
 *  - no BellMenu/UserMenu (no notifications, no profile surface — only logout)
 *  - 44px top strip with brand mark + page-context micro-label + discreet logout
 *  - main scrolls within viewport; body uses `bg-panel-1` to read as a
 *    factory-floor tablet rather than a marketing page
 *
 * Logout calls the worker-specific endpoint (`POST /api/field/auth/logout`)
 * so admin/pmo who land here by mistake can't accidentally tear down their
 * office session. Worker auth helpers proper will arrive in §3.3.
 */
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { LogOut } from "lucide-react";
import { api } from "@/api/client";
import { useInvalidateMe, useMe } from "@/hooks/useMe";
import { useSession } from "@/store/session";
import { clearQueue } from "@/lib/field-offline-queue";

interface FieldShellProps {
  /**
   * Micro-label справа от лого («СМЕНА» / «ОТЧЁТ»). `null` — не рендерим
   * точку и подпись вообще. §20/14.1: на `/field` контекст «смены» очевиден
   * из всего экрана, бренд-бар отдаём под date-чип; на `/field/sign` лейбл
   * пока сохраняется.
   */
  context: string | null;
  /**
   * Опциональный слот в правой части top-bar — между лого и кнопкой «ВЫХОД».
   * §20/14.1: сюда переезжает date-chip из ФИО-полосы.
   */
  topBarSlot?: React.ReactNode;
  children: React.ReactNode;
}

export function FieldShell({ context, topBarSlot, children }: FieldShellProps): React.ReactElement {
  const { t } = useTranslation();
  const clear = useSession((s) => s.clear);
  const invalidateMe = useInvalidateMe();
  const navigate = useNavigate();
  const { user } = useMe();

  const logoutMutation = useMutation({
    mutationFn: () => api.post<void>("/field/auth/logout"),
    onSettled: () => {
      // Always clear local session — even if the POST failed (e.g. session
      // already expired server-side) we want to drop the worker out.
      // Persistent offline-queue (см. §3.9) тоже чистим: если за PWA сядет
      // другой рабочий, его retry прилетит под чужой кукой.
      if (user) clearQueue(user.id);
      clear();
      invalidateMe();
      void navigate({ to: "/login" });
    },
  });

  return (
    <div
      data-testid="field-shell"
      className="flex h-screen flex-col overflow-hidden"
      style={{ background: "var(--bg-panel-1)" }}
    >
      <header
        data-testid="field-shell-header"
        className="relative flex h-11 shrink-0 items-center justify-between gap-3 px-4"
        style={{
          background: "var(--bg-panel-0)",
          borderBottom: "1px solid var(--rule-line)",
        }}
      >
        <div className="flex items-baseline gap-2">
          <span
            className="font-condensed text-[15px] font-semibold uppercase tracking-[0.08em]"
            style={{ color: "var(--fg-1)", fontFamily: "var(--font-display, inherit)" }}
          >
            {t("common.appName")}
          </span>
          {context !== null && (
            <>
              {/* Heat-bright dot — manufacturing-floor brand mark, 6px */}
              <span
                aria-hidden="true"
                className="inline-block h-[6px] w-[6px] rounded-full"
                style={{ background: "var(--accent-agent)" }}
              />
              <span
                className="font-condensed text-[11px] uppercase tracking-[0.18em]"
                style={{ color: "var(--fg-3)" }}
              >
                {context}
              </span>
            </>
          )}
        </div>

        {topBarSlot && (
          <div className="flex items-center" data-testid="field-shell-topbar-slot">
            {topBarSlot}
          </div>
        )}

        <button
          type="button"
          onClick={() => logoutMutation.mutate()}
          disabled={logoutMutation.isPending}
          data-testid="field-shell-logout"
          aria-label={t("field.shell.logout")}
          className="flex h-8 items-center gap-1.5 px-2 text-xs uppercase tracking-[0.12em] transition-colors disabled:opacity-50"
          style={{ color: "var(--fg-3)" }}
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          <span className="font-condensed">{t("field.shell.logout")}</span>
        </button>
      </header>

      <main data-testid="field-shell-main" className="min-h-0 flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  );
}
